import type { Kopplungsstand } from '@nac2s/shared'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { Input } from '@/components/base/input/input'
import { Knopf } from '@/teile/Knopf'
import { Status } from '@/teile/Status'
import { Fehler } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { kopplungCode, ladeKopplung } from '../api/partner.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Anbindung an netable (ADR-0020) — für Stacks, die schon eingerichtet sind:
 * Zustand der Kopplung und das Feld für einen neuen Kopplungscode. Der Code
 * wird hinterlegt; die Verwaltung holt ihn ab und koppelt. Solange er offen
 * ist, fragt die Seite im Takt nach. Nur für Konten mit `konten.verwalten`;
 * der Endpunkt prüft das ohnehin.
 */

const TAKT_MS = 5_000

export function Anbindung() {
  const { zustand: sitzung } = useSitzung()
  const darf =
    sitzung.status === 'angemeldet' && sitzung.sitzung.rechte.includes('konten.verwalten')
  const [stand, setStand] = useState<Kopplungsstand | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      setStand(await ladeKopplung())
      setFehler(null)
    } catch (e) {
      setFehler(e instanceof ApiError ? e.message : 'Nicht abrufbar.')
    }
  }, [])

  useEffect(() => {
    if (darf) void laden()
  }, [darf, laden])

  const codeOffen = Boolean(stand?.codeOffenBis)
  useEffect(() => {
    if (!darf || !codeOffen) return
    const uhr = setInterval(() => void laden(), TAKT_MS)
    return () => clearInterval(uhr)
  }, [darf, codeOffen, laden])

  if (!darf) return null

  async function hinterlegen(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const code = String(new FormData(ereignis.currentTarget).get('code') ?? '')
    setLaeuft(true)
    setMeldung(null)
    try {
      await kopplungCode(code)
      setMeldung('Code hinterlegt — netable koppelt diesen Server.')
      await laden()
    } catch {
      setMeldung('Der Code wurde nicht angenommen.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <section className="flex w-[22rem] max-w-full flex-col gap-3.5">
      <h2 className="font-display text-base font-bold text-primary">Anbindung an netable</h2>
      {fehler ? <Fehler>{fehler}</Fehler> : null}
      {stand ? (
        <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-sm">
          <dt className="text-quaternary">Zustand</dt>
          <dd>
            {stand.gekoppelt ? (
              <Status ton="gut">gekoppelt</Status>
            ) : stand.codeOffenBis ? (
              <Status ton="wartet">wartet auf netable</Status>
            ) : (
              <Status ton="wartet">nicht gekoppelt</Status>
            )}
          </dd>
          {stand.partnerName ? (
            <>
              <dt className="text-quaternary">Partner</dt>
              <dd>{stand.partnerName}</dd>
            </>
          ) : null}
          {stand.fingerabdruck ? (
            <>
              <dt className="text-quaternary">Server</dt>
              <dd className="font-mono text-[12px] break-all">{stand.fingerabdruck}</dd>
            </>
          ) : null}
          {stand.verwaltungsFingerabdruck ? (
            <>
              <dt className="text-quaternary">Verwaltung</dt>
              <dd className="font-mono text-[12px] break-all">{stand.verwaltungsFingerabdruck}</dd>
            </>
          ) : null}
          <dt className="text-quaternary">Fassung</dt>
          <dd className="tabular-nums">
            {stand.laufendeVersion}
            {stand.gewuenschteVersion && stand.gewuenschteVersion !== stand.laufendeVersion
              ? ` → ${stand.gewuenschteVersion} freigegeben`
              : ''}
          </dd>
          <dt className="text-quaternary">Letzte Abfrage</dt>
          <dd>
            {stand.letzteMeldung ? new Date(stand.letzteMeldung).toLocaleString('de-DE') : '—'}
          </dd>
          {stand.codeOffenBis ? (
            <>
              <dt className="text-quaternary">Code gültig bis</dt>
              <dd>{new Date(stand.codeOffenBis).toLocaleString('de-DE')}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      <form onSubmit={hinterlegen} className="flex flex-col gap-3">
        <Input
          isRequired
          name="code"
          label="Kopplungscode von netable"
          autoComplete="off"
          size="sm"
          placeholder="XXXX-XXXX-XXXX"
        />
        {meldung ? <p className="text-sm text-tertiary">{meldung}</p> : null}
        <div>
          <Knopf type="submit" disabled={laeuft}>
            {laeuft ? 'Wird hinterlegt …' : stand?.gekoppelt ? 'Neu koppeln' : 'Koppeln'}
          </Knopf>
        </div>
      </form>
    </section>
  )
}
